import { codeUnitString } from "./code-unit-string.js";
import { z } from "zod";
//#region src/app-logs.ts
/**
* App-scoped log query wire types — GET /v1/admin/apps/:appId/logs
* (site-admin-api observability module; App-Developer MCP Server M8).
*
* `:appId` is the `release.apps.id` UUID (a PATH param, resource-scoped by
* `@RequireResourcePermission('cp:app:read','appId')`); it is NOT part of this
* query schema — `appLogsQuerySchema` carries only the filterable query-string
* params (parity with `adoptionQuerySchema`, which likewise omits the path
* appId). `.strict()` (#466) rejects unknown query keys; `limit` is
* `z.coerce.number()` because it arrives as a query string. Bounded results
* only (AC4): the server defaults `limit` to 100 and hard-caps it at 1000.
*
* `AppLogsQuery`'s `.shape` is the single source of truth WS-mcp-tools-
* observability composes the `get_app_logs` MCP tool inputSchema from (M4);
* see that workstream's plan for how it adds the `appId` field.
*/
/**
* Filterable query params. `level`/`search` are user-controlled and are
* allowlisted / escaped SERVER-SIDE (site-admin-api `logql-safety.ts`) before
* they reach LogQL — the boundary only bounds their length/type here. `start`/
* `end` are ISO-8601 datetimes (rejected at the boundary if malformed).
*/
const appLogsQuerySchema = z.object({
	/**
	* **REQUIRED** — the data plane to read from (**R4-07**).
	*
	* `design/02` §2.2 E7 says an omitted `dpId` means *"every DP the app runs
	* on (central store only, §4)"*. **There is no such substrate.** The spine
	* deferred central/fleet-wide log search and §4's items are not in R4, so
	* the fleet path has nothing to read from. Fanning out across every DP
	* instead would contradict design rule R3 (*"Never fan out for a list"*).
	*
	* So omitting it is a 400 that NAMES the reason rather than a request that
	* quietly returns one DP's logs, or none. The custom `error` below is the
	* whole point of making it required this way: zod's default message for an
	* absent field is *"expected string, received undefined"*, which tells a
	* caller nothing about why the field became mandatory.
	*/
	dpId: z.uuid({ error: (issue) => issue.input === void 0 ? "dpId is required: app-scoped log search is per-data-plane. Fleet-wide search across every DP an app runs on has no backing store yet (it is deferred, not merely unimplemented), so a query without a dpId cannot be answered. Pass the id of the data plane you want to read." : "dpId must be a UUID identifying one data plane." }),
	/** Single canonical log level (TRACE/DEBUG/INFO/WARN/WARNING/ERROR/FATAL,
	*  any casing). Allowlisted server-side; a non-allowlisted value → 400. */
	level: z.string().min(1).max(16).optional(),
	/** Free-text substring match. Escaped into a LogQL `|=` literal server-side.
	*  Length-bounded to keep the query cheap (ReDoS/oversized-query defense). */
	search: z.string().min(1).max(1024).optional(),
	/** ISO-8601 lower time bound. Defaults to now − 1h when omitted. */
	start: z.string().datetime().optional(),
	/** ISO-8601 upper time bound. Defaults to now when omitted. */
	end: z.string().datetime().optional(),
	/** Page size; server clamps to ≤1000, default 100 (AC4 — no unbounded dump). */
	limit: z.coerce.number().int().min(1).max(1e3).optional(),
	/** Opaque continuation cursor from a previous page's `nextCursor`. Its caps count UTF-16 code
	*  units, so the 1,024-unit cap of a read-audit row (`readAuditDetailsFieldsSchema`) holds. */
	cursor: codeUnitString().min(1).optional(),
	/**
	* #543 — restrict the query to one fixture's log streams.
	*
	* ⚠️ **RENAMED from `fixture`, and now actually CONSUMED.** design/02 §2.2
	* E7 called for both: the old name was accepted by the strict schema and
	* forward-plumbed by the client while *"the server never reads it"* — a
	* field that looked like a filter and silently was not. It now narrows the
	* server-composed pod-name scope to `<slug>--<fixtureId>--…`.
	*
	* The rename is a breaking wire change (`.strict()` 400s the old key), so
	* it ships in the SAME change as the `@zion/sdk-node` + `zion-app` update
	* that emits it — never before.
	*/
	fixtureId: z.string().min(1).max(63).optional(),
	/**
	* Narrow to one deployed version. Requires `fixtureId` — the pod-name scope
	* is hierarchical (`<slug>--<fixtureId>--<versionSlug>-…`) and a level
	* cannot be skipped; omitting the fixture is a 400.
	*/
	versionSlug: z.string().min(1).max(16).optional(),
	/**
	* Narrow to one exact pod. Must lie inside the scope the other fields
	* describe — a pod outside it is a 400, not an empty result, because
	* answering the pod while silently discarding a contradictory `fixtureId`
	* would return rows the caller's own filter excluded.
	*/
	pod: z.string().min(1).max(253).optional(),
	/** Narrow to one container within the matched pods. DNS-1123 label. */
	container: z.string().min(1).max(63).optional(),
	/**
	* Scan direction. Absent ⇒ `backward` (newest-first), which is what every
	* caller got before this field existed, so omitting it is byte-identical to
	* pre-R4 behaviour.
	*
	* ⚠️ THIS IS THE PUBLIC, APP-OWNER-FACING FIELD. There is a SECOND,
	* DIFFERENT `direction` on the CP↔DP wire (`@zion/sdk-cp-dp-contracts`
	* `internal-bootstrap.schemas.ts` `GetLogsQuery`). They are separate fields
	* in separate packages: this one is what a caller sends to CP; that one is
	* what CP forwards to the DP after its own validation. Do not conflate them,
	* and do not assume a change to one propagates to the other.
	*
	* ⛔ `direction` interacts with CURSOR PLACEMENT and the two must be ruled on
	* together. Paging backward wants the cursor on the window's UPPER bound;
	* paging forward wants it on the LOWER bound. The shipped CP behaviour today
	* DROPS a nanosecond-epoch cursor rather than forwarding it as an absolute
	* `start`, because forwarding it under `direction: backward` makes "Load
	* more" return zero rows (R2-08). Adding `forward` without revisiting that
	* placement re-opens the same class of bug from the other side.
	*/
	direction: z.enum(["forward", "backward"]).optional(),
	/**
	* ⭐ **AGGREGATE MODE (spine V-4).** When present, the response is a COUNT, not
	* lines: `{ appId, dpId, mode, total, byLevel?, window }` and **no `entries`
	* field exists in that schema at all** — structurally impossible to drag log
	* payloads through an agent's context, which is the entire point. The field
	* report's complaint was a 143 KB response where one integer was wanted.
	*
	* - `'count'` — one number: how many lines matched.
	* - `'count_by_level'` — the same number, plus a per-level breakdown.
	*
	* ⛔ **`level`, `search`, `fixtureId`, `versionSlug`, `pod`, `container`,
	* `start` and `end` all still apply** and narrow the count exactly as they
	* narrow the lines. That is what makes the count trustworthy: it answers "how
	* many of the lines I would have got", not "how many lines exist".
	*
	* ⛔ **`cursor`, `limit` and `direction` are REJECTED alongside `aggregate`** —
	* a 400 naming the conflict, not a silently-ignored parameter. A count has no
	* page, no page size and no scan direction; accepting them would advertise
	* paging over a scalar. See `AppLogsService.query`'s refusal.
	*
	* ⚠️ There is no `truncated` in the aggregate response: R-1(3) makes this an
	* INSTANT query of `sum(count_over_time(...))`, a vector result with no limit
	* semantics, so there is nothing to truncate. ⛔ Do not add one "for
	* consistency" — R-4's meaning ("more existed than you received") would be
	* unfalsifiable on a value that cannot be cut.
	*
	* ⛔ `group-by-message-template` is **excluded, written** (spine V-4): Loki
	* pattern extraction is version-gated and approximate, and a wrong template
	* grouping is worse than none — an agent shown three buckets for one error
	* believes it has three problems.
	*/
	aggregate: z.enum(["count", "count_by_level"]).optional()
}).strict();
/** One log line. Mirrors the DP `LogEntry` shape (timestamp/pod/container/level/message[/traceId]). */
const appLogEntrySchema = z.object({
	/**
	* **ISO-8601**, converted at the CP boundary.
	*
	* ⚠️ The CP↔DP wire carries a Loki NANOSECOND-EPOCH string here, explicitly
	* documented as *"NOT an ISO string — consumers must not `new Date(...)`-parse
	* it"*. This developer-facing field is the converted form; the raw
	* nanosecond value survives only inside the opaque `nextCursor`, where its
	* precision is load-bearing.
	*/
	timestamp: z.string(),
	/** The data plane this line came from. Echoes the requested `dpId`. */
	dpId: z.uuid(),
	pod: z.string(),
	container: z.string(),
	/**
	* Parsed from the pod name against the operator's naming spine, or `null`
	* when the name is not app-shaped. `null` is a real answer — the line is
	* still returned.
	*/
	fixtureId: z.string().nullable(),
	versionSlug: z.string().nullable(),
	level: z.string(),
	message: z.string(),
	traceId: z.string().optional()
}).strict();
/** Bounded, cursor-paginated log page for one app. */
const appLogsResponseSchema = z.object({
	/**
	* Echoes the requested app **UUID** (`release.apps.id`) — i.e. the value of
	* the `:appId` path param, verbatim. It is NOT the app slug (the slug is only
	* used internally to compose the pod-name stream selector — see the service's
	* UUID→slug resolution). Consumers key on this UUID, matching the request.
	*/
	appId: z.string().uuid(),
	entries: z.array(appLogEntrySchema),
	/** Continuation cursor, or `null` when there is no next page. */
	nextCursor: z.string().nullable()
}).strict();
/** The `[start, end]` the count covers — ISO-8601, echoed so a caller knows what it asked. */
const appLogAggregateWindowSchema = z.object({
	start: z.string().datetime(),
	end: z.string().datetime()
}).strict();
/**
* Per-level counts.
*
* ## ⛔ THE KEY DOMAIN IS CLOSED, NORMALIZED, AND OURS (B-3, R-41.4)
*
* ⚠️ **An earlier revision justified an UNBOUNDED `z.record(z.string(), …)` by
* citing `loki-proxy-client.impl.ts:152` (`stream['level'] ?? …`). That citation
* is about the LINE path's per-entry level — a DIFFERENT PRODUCER.** These buckets
* are not observed labels: per R-1(2) the DP issues **one instant query per level
* over our own normalized allowlist**, so a bucket can only exist if we asked for
* it. The domain is therefore closed, and ours, and pretending otherwise cost two
* real defects:
*
*  1. **`Σ(byLevel) ≠ total` was reachable and unstated.** The unfiltered `total`
*     counts every line; a line carrying a non-canonical level (`level="critical"`
*     is a producer-controlled stream LABEL) is selected by NO allowlisted level
*     filter, so it landed in `total` and in **no bucket**. An agent summing the
*     buckets, or reading `error: 0` beside a large `total`, got a false all-clear
*     with nothing to point at — strictly worse than the `unknown` case, because
*     `unknown` at least has a bucket.
*  2. **An unbounded DP-controlled map on the surface built because of a 143 KB
*     response.** No charset, length or cardinality bound, on the one collection
*     here that carries no `truncated`. And `level` INBOUND is
*     `z.string().min(1).max(16)` while the keys coming back were unbounded — an
*     asymmetry with no reason behind it.
*
* ⇒ **Three changes, and they are one change:**
*
* - **Keys are bounded and normalized-lowercase.** The regex is the shape
*   `normalizeLevel` produces, so a key it cannot produce is a parse failure at
*   the CP boundary rather than a row in an agent's context. A `constructor` key
*   from a producer-controlled label is rejected by the `.max(16)` + anchor pair
*   like any other non-level key.
*
*   ⚠️⚠️ **`__proto__` IS HANDLED, BUT NOT BY THIS REGEX, AND THE DIFFERENCE IS
*   WRITTEN DOWN BECAUSE THE NEXT EDITOR WILL ASSUME OTHERWISE (dev-ctr3 E-3).**
*   An earlier revision of this docblock claimed *"the leading-letter anchor is
*   what makes `__proto__` unrepresentable."* **MEASURED on zod 4.4.x and that is
*   false:** `z.record` **silently DROPS** an own `__proto__` key *before* the key
*   schema runs, so `safeParse` **SUCCEEDS** and the key is simply absent from the
*   output. Controls run alongside: `INFO` IS rejected, `__proto__` + `BAD`
*   together IS rejected, and a plain
*   `z.record(z.string().regex(/^[a-z]+$/), z.number())` **also** accepts
*   `__proto__` — which isolates the behaviour to zod rather than to our pattern.
*   ⇒ The object-key hazard is closed (the key can never reach `byLevel[k] = …`,
*   and the parsed output's prototype is clean), but it is closed by the LIBRARY.
*   ⛔ Do not weaken the anchor believing it is the `__proto__` guard, and do not
*   "restore" the rejection claim — it was measured false.
* - **`other` is a RESIDUAL BUCKET, not a leak.** `other = total − Σ(canonical)`,
*   so a line no allowlisted filter selects is **VISIBLE** rather than absent.
*   ⭐ This is the previous revision's own argument (*"a bucket we cannot name is
*   still a bucket an operator needs to see"*) turned the right way round: we
*   cannot name the LEVEL, so we name the RESIDUAL.
* - ⇒ **`Σ(byLevel) === total` becomes an INVARIANT** (I-20b), because `other`
*   makes it satisfiable. Without `other` it is false by construction.
*
* ⚠️ **`unknown` is NOT `other`, and conflating them undoes the `unknown`
* argument.** `unknown` is a REAL, allowlisted Loki `detected_level` (measured:
* 103 of 300 streams on live GKE `dp-app`) with its own query and its own bucket.
* `other` is the residual for levels outside the allowlist entirely. A reader who
* merges them loses the unstructured tail, which is the third of the data this
* whole section exists for.
*
* ⛔ **The domain lives in ONE place and it is NOT here (R-57 as re-ruled by
* R-64).** The normalized allowlist is `NORMALIZED_LOG_LEVELS`, exported from
* `@zion/sdk-cp-dp-contracts`' `src/log-levels.ts`. ⚠️ This schema does **not**
* import it — `@zion/sdk-contracts` is a PUBLISHED SDK whose only dependency is
* `zod` (measured), and adding an internal CP↔DP package to its graph is a
* dep-budget change R-37 explicitly rules out. ⇒ The schema bounds the key SHAPE;
* the exact-set assertion against `NORMALIZED_LOG_LEVELS` lives in
* **site-admin-api**'s TEST-AGG-7, which already depends on that package. Two
* halves, both required, neither duplicated.
*/
const appLogLevelCountsSchema = z.record(z.string().min(1).max(16).regex(/^[a-z][a-z0-9_-]*$/), z.number().int().nonnegative());
/**
* The aggregate DATA — shared by `get_app_logs`' aggregate mode and by
* `get_app_problems`' `errorRate` section (`app-problems.ts` imports this).
*
* ⚠️ **`byLevel` is OPTIONAL, per the lead's stated shape** (`{ total, byLevel?,
* window }`, spine V-4 as amended by R-1). It is present iff the request asked for
* `count_by_level`. ⛔ That invariant is NOT expressible in this schema and is
* therefore PINNED BY TEST (site-admin-api TEST-AGG-4/5) — recorded so nobody
* reads the optionality as "sometimes we forget". A discriminated union on `mode`
* would have made it structural, and was deliberately NOT used because the ruling
* names this shape; the test is the compensating control.
*/
const appLogAggregateSchema = z.object({
	/** Lines matching the (unfiltered-by-level) query in the window. */
	total: z.number().int().nonnegative(),
	/**
	* Present iff `mode === 'count_by_level'`.
	*
	* ⛔ **The FULL normalized domain, zeros included, plus `other`** (R-41.4).
	* A level with no lines appears as `0`; it is not omitted. That is what lets
	* an agent tell *"zero errors"* from *"`error` is not a thing on this
	* platform"*, which is this program's whole thesis. ⇒ `Σ(byLevel) === total`
	* (I-20b), and TEST-AGG-7's key-set assertion is an EXACT SET, not a subset —
	* a subset assertion passes against a producer that drops a bucket.
	*/
	byLevel: appLogLevelCountsSchema.optional(),
	/**
	* ⛔⛔ **R-39.2 — `false` ⇔ the selector matched NO LOG STREAMS AT ALL.**
	*
	* ⭐ **This one bit is the difference between this contract and C-1.** Without
	* it `{total: 0}` is the only thing a broken collector can say, and `total: 0`
	* reads as *"your app was quiet for an hour"* — which is verbatim the sentence
	* `get_app_status` was DELETED for rendering. `{total, byLevel?, window}`
	* `.strict()` could not express *"nothing was read"*, so it could not avoid
	* saying *"nothing happened"*.
	*
	* ⚠️ **The producer can already tell them apart and this field is where it
	* says so.** R-1(3)'s instant query returns an **empty vector** when no stream
	* matches the selector, and a vector carrying a value when streams matched and
	* counted N lines — including N = 0. One line at the DP, no extra query.
	*
	* ⇒ Consumers: `get_app_problems`' `errorRate` maps `false` to
	* `{read: false, reason: 'no_log_streams'}` — so on that section's `read: true`
	* arm this is ALWAYS `true` (I-25). `get_app_logs`' aggregate mode has no
	* `read` wrapper, so it carries the bit on the wire and its description says
	* what it means.
	*
	* ⛔ **Do NOT default it, and do not make it optional.** A defaulted
	* `matchedStreams: true` is indistinguishable from a DP that never learned to
	* send it, which is the failure mode this field exists to make impossible.
	*/
	matchedStreams: z.boolean(),
	window: appLogAggregateWindowSchema
}).strict();
/**
* `GET /v1/admin/apps/:appId/logs?aggregate=…` — the counts response.
*
* ⛔⛔ **THERE IS NO `entries` FIELD AND NO `lines` FIELD, AND THERE NEVER WILL BE**
* (spine V-4). `.strict()` makes adding one a parse failure at every consumer, and
* {@link _appLogsAggregateCarriesNoPayload} makes it a compile error.
*
* ⛔ A SEPARATE schema, not `appLogsResponseSchema` with fields made optional —
* optional fields are precisely how `entries` comes back. ⚠️ MEASURED:
* `appLogsResponseSchema` has FIVE consumer packages beyond site-admin-api, one of
* them a published SDK (`@zion/sdk-node`), so widening it into a union would be a
* breaking type change across all of them for a mode none of them sends.
*/
const appLogsAggregateResponseSchema = z.object({
	/** Echoes the requested app UUID, exactly as the lines response does. ⚠️ `z.uuid()`, the house form (H-4). */
	appId: z.uuid(),
	/** Echoes the requested `dpId`. The lines response omits it; a count has no per-line dpId to carry, so it goes here. */
	dpId: z.uuid(),
	/** Echoes the requested mode, so the answer is self-describing. */
	mode: z.enum(["count", "count_by_level"]),
	total: z.number().int().nonnegative(),
	byLevel: appLogLevelCountsSchema.optional(),
	/** ⛔ R-39.2 — see {@link appLogAggregateSchema}. `false` ⇒ no streams matched; NOT "quiet". */
	matchedStreams: z.boolean(),
	window: appLogAggregateWindowSchema
}).strict();
//#endregion
export { appLogAggregateSchema, appLogAggregateWindowSchema, appLogEntrySchema, appLogLevelCountsSchema, appLogsAggregateResponseSchema, appLogsQuerySchema, appLogsResponseSchema };

//# sourceMappingURL=app-logs.js.map